import { CalendarDate, CalendarDateTime } from '@internationalized/date';
import {
    createProperties,
    createRoot,
    type Field,
    Form,
    type FormState,
    type TypeToFields,
} from '@jmvdevelop/svelte-form';
import type { ShareableContainer } from '$lib/container/shareableContainer';
import { notNullableOrThrow } from '$lib/utils/assert';
import { mergeObject } from '$lib/utils/mergeObject';
import { fail, ok, type Result } from '$lib/utils/result.js';
import { temporizePromise } from '$lib/utils/temporizePromise.js';
import { ImmURL } from '$lib/utils/url/ImmURL';
import { getUtmVariablesFromUrl } from '$lib/utils/utmVariables';
import type { JsonValue } from '$lib/utils/zod';
import { validateEmail } from '$lib/validator/email';
import { notBlankAndNotNull, notNull } from '$lib/validator/validator.js';
import {
    MultiQuote_CreateMutationDocument,
    type MultiQuote_CreateMutationMutation,
} from '$gql/graphql.js';
import { getRequestInfoInput } from '$components/Ad/utils';
import AgenceVoyageForm from '$components/MultiQuoteForm/AgenceVoyage/AgenceVoyageForm.svelte';
import * as AgenceVoyage from '$components/MultiQuoteForm/AgenceVoyage/AgenceVoyageFormData';
import AmeublementForm from '$components/MultiQuoteForm/Ameublement/AmeublementForm.svelte';
import * as Ameublement from '$components/MultiQuoteForm/Ameublement/AmeublementFormData';
import ArchitecteForm from '$components/MultiQuoteForm/Architecte/ArchitecteForm.svelte';
import * as Architecte from '$components/MultiQuoteForm/Architecte/ArchitecteFormData';
import AssuranceForm from '$components/MultiQuoteForm/Assurance/AssuranceForm.svelte';
import * as Assurance from '$components/MultiQuoteForm/Assurance/AssuranceFormData';
import CarRentalForm from '$components/MultiQuoteForm/CarRental/CarRentalForm.svelte';
import * as CarRental from '$components/MultiQuoteForm/CarRental/CarRentalFormData';
import ConstructionForm from '$components/MultiQuoteForm/Construction/ConstructionForm.svelte';
import * as Construction from '$components/MultiQuoteForm/Construction/ConstructionFormData';
import ConstructionMetalliqueForm from '$components/MultiQuoteForm/ConstructionMetallique/ConstructionMetalliqueForm.svelte';
import * as ConstructionMetallique from '$components/MultiQuoteForm/ConstructionMetallique/ConstructionMetalliqueFormData';
import ConsultantForm from '$components/MultiQuoteForm/Consultant/ConsultantForm.svelte';
import * as Consultant from '$components/MultiQuoteForm/Consultant/ConsultantFormData';
import EnseignementSuperieurForm from '$components/MultiQuoteForm/EnseignementSuperieur/EnseignementSuperieurForm.svelte';
import * as EnseignementSuperieur from '$components/MultiQuoteForm/EnseignementSuperieur/EnseignementSuperieurFormData';
import EquipementMedicalForm from '$components/MultiQuoteForm/EquipementMedical/EquipementMedicalForm.svelte';
import * as EquipementMedical from '$components/MultiQuoteForm/EquipementMedical/EquipementMedicalFormData';
import ExpertComptableForm from '$components/MultiQuoteForm/ExpertComptable/ExpertComptableForm.svelte';
import * as ExpertComptable from '$components/MultiQuoteForm/ExpertComptable/ExpertComptableFormData';
import FroidClimatisationForm from '$components/MultiQuoteForm/FroidClimatisation/FroidClimatisationForm.svelte';
import * as FroidClimatisation from '$components/MultiQuoteForm/FroidClimatisation/FroidClimatisationFormData';
import FormationProForm from '$components/MultiQuoteForm/FormationPro/FormationProForm.svelte';
import * as FormationPro from '$components/MultiQuoteForm/FormationPro/FormationProFormData';
import GarageMecaniqueForm from '$components/MultiQuoteForm/GarageMecanique/GarageMecaniqueForm.svelte';
import * as GarageMecanique from '$components/MultiQuoteForm/GarageMecanique/GarageMecaniqueFormData';
import GardiennageSecuriteForm from '$components/MultiQuoteForm/GardiennageSecurite/GardiennageSecuriteForm.svelte';
import * as GardiennageSecurite from '$components/MultiQuoteForm/GardiennageSecurite/GardiennageSecuriteFormData';
import ImmobilierForm from '$components/MultiQuoteForm/Immobilier/ImmobilierForm.svelte';
import * as Immobilier from '$components/MultiQuoteForm/Immobilier/ImmobilierFormData';
import LaboratoireBtpForm from '$components/MultiQuoteForm/LaboratoireBtp/LaboratoireBtpForm.svelte';
import * as LaboratoireBtp from '$components/MultiQuoteForm/LaboratoireBtp/LaboratoireBtpFormData';
import NettoyageIndustrielForm from '$components/MultiQuoteForm/NettoyageIndustriel/NettoyageIndustrielForm.svelte';
import * as NettoyageIndustriel from '$components/MultiQuoteForm/NettoyageIndustriel/NettoyageIndustrielFormData';
import OfficeFurnitureForm from '$components/MultiQuoteForm/OfficeFurniture/OfficeFurnitureForm.svelte';
import * as OfficeFurniture from '$components/MultiQuoteForm/OfficeFurniture/OfficeFurnitureFormData';
import SolarForm from '$components/MultiQuoteForm/Solar/SolarForm.svelte';
import * as Solar from '$components/MultiQuoteForm/Solar/SolarFormData';
import TransitForm from '$components/MultiQuoteForm/Transit/TransitForm.svelte';
import * as Transit from '$components/MultiQuoteForm/Transit/TransitFormData';
import VisaEtudeForm from '$components/MultiQuoteForm/VisaEtude/VisaEtudeForm.svelte';
import * as VisaEtude from '$components/MultiQuoteForm/VisaEtude/VisaEtudeFormData';
import MenuiserieAluminiumForm from '$components/MultiQuoteForm/MenuiserieAluminium/MenuiserieAluminiumForm.svelte';
import * as MenuiserieAluminium from '$components/MultiQuoteForm/MenuiserieAluminium/MenuiserieAluminiumFormData';
import ImprimerieForm from '$components/MultiQuoteForm/Imprimerie/ImprimerieForm.svelte';
import * as Imprimerie from '$components/MultiQuoteForm/Imprimerie/ImprimerieFormData';
import IngenierieInformatiqueForm from '$components/MultiQuoteForm/IngenierieInformatique/IngenierieInformatiqueForm.svelte';
import * as IngenierieInformatique from '$components/MultiQuoteForm/IngenierieInformatique/IngenierieInformatiqueFormData';
import MaterielInformatiqueForm from '$components/MultiQuoteForm/MaterielInformatique/MaterielInformatiqueForm.svelte';
import * as MaterielInformatique from '$components/MultiQuoteForm/MaterielInformatique/MaterielInformatiqueFormData';
import ElectricienForm from '$components/MultiQuoteForm/Electricien/ElectricienForm.svelte';
import * as Electricien from '$components/MultiQuoteForm/Electricien/ElectricienFormData';
import AmenagementFoncierForm from '$components/MultiQuoteForm/AmenagementFoncier/AmenagementFoncierForm.svelte';
import * as AmenagementFoncier from '$components/MultiQuoteForm/AmenagementFoncier/AmenagementFoncierFormData';
import DeratisationDesinsectisationForm from '$components/MultiQuoteForm/DeratisationDesinsectisation/DeratisationDesinsectisationForm.svelte';
import * as DeratisationDesinsectisation from '$components/MultiQuoteForm/DeratisationDesinsectisation/DeratisationDesinsectisationFormData';
import BanqueForm from '$components/MultiQuoteForm/Banque/BanqueForm.svelte';
import * as Banque from '$components/MultiQuoteForm/Banque/BanqueFormData';
import MateriauxConstructionForm from '$components/MultiQuoteForm/MateriauxConstruction/MateriauxConstructionForm.svelte';
import * as MateriauxConstruction from '$components/MultiQuoteForm/MateriauxConstruction/MateriauxConstructionFormData';
import HotelsForm from '$components/MultiQuoteForm/Hotels/HotelsForm.svelte';
import * as Hotels from '$components/MultiQuoteForm/Hotels/HotelsFormData';
import PharmaciesForm from '$components/MultiQuoteForm/Pharmacies/PharmaciesForm.svelte';
import * as Pharmacies from '$components/MultiQuoteForm/Pharmacies/PharmaciesFormData';
import ConcessionnairesAutomobileForm from '$components/MultiQuoteForm/ConcessionnairesAutomobile/ConcessionnairesAutomobileForm.svelte';
import * as ConcessionnairesAutomobile from '$components/MultiQuoteForm/ConcessionnairesAutomobile/ConcessionnairesAutomobileFormData';
import GroupeElectrogeneForm from '$components/MultiQuoteForm/GroupeElectrogene/GroupeElectrogeneForm.svelte';
import * as GroupeElectrogene from '$components/MultiQuoteForm/GroupeElectrogene/GroupeElectrogeneFormData';
import ArchitectesInterieurForm from '$components/MultiQuoteForm/ArchitectesInterieur/ArchitectesInterieurForm.svelte';
import * as ArchitectesInterieur from '$components/MultiQuoteForm/ArchitectesInterieur/ArchitectesInterieurFormData';
import AgencesCommunicationForm from '$components/MultiQuoteForm/AgencesCommunication/AgencesCommunicationForm.svelte';
import * as AgencesCommunication from '$components/MultiQuoteForm/AgencesCommunication/AgencesCommunicationFormData';
import MaterielElectriqueForm from '$components/MultiQuoteForm/MaterielElectrique/MaterielElectriqueForm.svelte';
import * as MaterielElectrique from '$components/MultiQuoteForm/MaterielElectrique/MaterielElectriqueFormData';
import MaterielsBTPForm from '$components/MultiQuoteForm/MaterielsBTP/MaterielsBTPForm.svelte';
import * as MaterielsBTP from '$components/MultiQuoteForm/MaterielsBTP/MaterielsBTPFormData';
import MaterielsIndustrielsForm from '$components/MultiQuoteForm/MaterielsIndustriels/MaterielsIndustrielsForm.svelte';
import * as MaterielsIndustriels from '$components/MultiQuoteForm/MaterielsIndustriels/MaterielsIndustrielsFormData';
import AgencesRecrutementForm from '$components/MultiQuoteForm/AgencesRecrutement/AgencesRecrutementForm.svelte';
import * as AgencesRecrutement from '$components/MultiQuoteForm/AgencesRecrutement/AgencesRecrutementFormData';
import CliniquesForm from '$components/MultiQuoteForm/Cliniques/CliniquesForm.svelte';
import * as Cliniques from '$components/MultiQuoteForm/Cliniques/CliniquesFormData';
import ConseilJuridiqueForm from '$components/MultiQuoteForm/ConseilJuridique/ConseilJuridiqueForm.svelte';
import * as ConseilJuridique from '$components/MultiQuoteForm/ConseilJuridique/ConseilJuridiqueFormData';
import type { OnSubmitSuccessParams } from '$components/Form/HtmlForm.svelte';

export type MultiQuoteFormData = {
    user: number | null;
    message: string;
    categoryId: number | null;
    relatedCmsPostId: number | null;
    country: string | null;
    city: number | null;
    firstname: string | null;
    lastname: string | null;
    email: string | null;
    phone: string | null;
    agenceVoyage: AgenceVoyage.AgenceVoyageFormData;
    ameublement: Ameublement.AmeublementFormData;
    carRental: CarRental.CarRentalFormData;
    assurance: Assurance.AssuranceFormData;
    transit: Transit.TransitFormData;
    solar: Solar.SolarFormData;
    consultant: Consultant.ConsultantFormData;
    officeFurniture: OfficeFurniture.OfficeFurnitureFormData;
    construction: Construction.ConstructionFormData;
    constructionMetallique: ConstructionMetallique.ConstructionMetalliqueFormData;
    froidClimatisation: FroidClimatisation.FroidClimatisationFormData;
    visaEtude: VisaEtude.VisaEtudeFormData;
    gardiennageSecurite: GardiennageSecurite.GardiennageSecuriteFormData;
    architecte: Architecte.ArchitecteFormData;
    nettoyageIndustriel: NettoyageIndustriel.NettoyageIndustrielFormData;
    expertComptable: ExpertComptable.ExpertComptableFormData;
    equipementMedical: EquipementMedical.EquipementMedicalFormData;
    formationPro: FormationPro.FormationProFormData;
    enseignementSuperieur: EnseignementSuperieur.EnseignementSuperieurFormData;
    garageMecanique: GarageMecanique.GarageMecaniqueFormData;
    laboratoireBtp: LaboratoireBtp.LaboratoireBtpFormData;
    immobilier: Immobilier.ImmobilierFormData;
    menuiserieAluminium: MenuiserieAluminium.MenuiserieAluminiumFormData;
    imprimerie: Imprimerie.ImprimerieFormData;
    ingenierieInformatique: IngenierieInformatique.IngenierieInformatiqueFormData;
    materielInformatique: MaterielInformatique.MaterielInformatiqueFormData;
    electricien: Electricien.ElectricienFormData;
    amenagementFoncier: AmenagementFoncier.AmenagementFoncierFormData;
    deratisationDesinsectisation: DeratisationDesinsectisation.DeratisationDesinsectisationFormData;
    banque: Banque.BanqueFormData;
    materiauxConstruction: MateriauxConstruction.MateriauxConstructionFormData;
    hotels: Hotels.HotelsFormData;
    pharmacies: Pharmacies.PharmaciesFormData;
    concessionnairesAutomobile: ConcessionnairesAutomobile.ConcessionnairesAutomobileFormData;
    groupeElectrogene: GroupeElectrogene.GroupeElectrogeneFormData;
    architectesInterieur: ArchitectesInterieur.ArchitectesInterieurFormData;
    agencesCommunication: AgencesCommunication.AgencesCommunicationFormData;
    materielElectrique: MaterielElectrique.MaterielElectriqueFormData;
    materielsBTP: MaterielsBTP.MaterielsBTPFormData;
    materielsIndustriels: MaterielsIndustriels.MaterielsIndustrielsFormData;
    agencesRecrutement: AgencesRecrutement.AgencesRecrutementFormData;
    cliniques: Cliniques.CliniquesFormData;
    conseilJuridique: ConseilJuridique.ConseilJuridiqueFormData;
};

type FormData = MultiQuoteFormData;

export type Fields<TFormValues> = TypeToFields<
    TFormValues,
    FormData,
    keyof FormData,
    {
        agenceVoyage: AgenceVoyage.Fields<TFormValues>;
        ameublement: Ameublement.Fields<TFormValues>;
        carRental: CarRental.Fields<TFormValues>;
        assurance: Assurance.Fields<TFormValues>;
        transit: Transit.Fields<TFormValues>;
        solar: Solar.Fields<TFormValues>;
        consultant: Consultant.Fields<TFormValues>;
        officeFurniture: OfficeFurniture.Fields<TFormValues>;
        construction: Construction.Fields<TFormValues>;
        constructionMetallique: ConstructionMetallique.Fields<TFormValues>;
        froidClimatisation: FroidClimatisation.Fields<TFormValues>;
        visaEtude: VisaEtude.Fields<TFormValues>;
        gardiennageSecurite: GardiennageSecurite.Fields<TFormValues>;
        architecte: Architecte.Fields<TFormValues>;
        nettoyageIndustriel: NettoyageIndustriel.Fields<TFormValues>;
        expertComptable: ExpertComptable.Fields<TFormValues>;
        equipementMedical: EquipementMedical.Fields<TFormValues>;
        formationPro: FormationPro.Fields<TFormValues>;
        enseignementSuperieur: EnseignementSuperieur.Fields<TFormValues>;
        garageMecanique: GarageMecanique.Fields<TFormValues>;
        laboratoireBtp: LaboratoireBtp.Fields<TFormValues>;
        immobilier: Immobilier.Fields<TFormValues>;
        menuiserieAluminium: MenuiserieAluminium.Fields<TFormValues>;
        imprimerie: Imprimerie.Fields<TFormValues>;
        ingenierieInformatique: IngenierieInformatique.Fields<TFormValues>;
        materielInformatique: MaterielInformatique.Fields<TFormValues>;
        electricien: Electricien.Fields<TFormValues>;
        amenagementFoncier: AmenagementFoncier.Fields<TFormValues>;
        deratisationDesinsectisation: DeratisationDesinsectisation.Fields<TFormValues>;
        banque: Banque.Fields<TFormValues>;
        materiauxConstruction: MateriauxConstruction.Fields<TFormValues>;
        hotels: Hotels.Fields<TFormValues>;
        pharmacies: Pharmacies.Fields<TFormValues>;
        concessionnairesAutomobile: ConcessionnairesAutomobile.Fields<TFormValues>;
        groupeElectrogene: GroupeElectrogene.Fields<TFormValues>;
        architectesInterieur: ArchitectesInterieur.Fields<TFormValues>;
        agencesCommunication: AgencesCommunication.Fields<TFormValues>;
        materielElectrique: MaterielElectrique.Fields<TFormValues>;
        materielsBTP: MaterielsBTP.Fields<TFormValues>;
        materielsIndustriels: MaterielsIndustriels.Fields<TFormValues>;
        agencesRecrutement: AgencesRecrutement.Fields<TFormValues>;
        cliniques: Cliniques.Fields<TFormValues>;
        conseilJuridique: ConseilJuridique.Fields<TFormValues>;
    }
>;

export const empty: FormData = {
    user: null,
    message: '',
    categoryId: null,
    country: null,
    city: null,
    phone: null,
    firstname: null,
    lastname: null,
    email: null,
    relatedCmsPostId: null,
    agenceVoyage: AgenceVoyage.empty,
    ameublement: Ameublement.empty,
    carRental: CarRental.empty,
    assurance: Assurance.empty,
    transit: Transit.empty,
    solar: Solar.empty,
    consultant: Consultant.empty,
    officeFurniture: OfficeFurniture.empty,
    construction: Construction.empty,
    constructionMetallique: ConstructionMetallique.empty,
    froidClimatisation: FroidClimatisation.empty,
    visaEtude: VisaEtude.empty,
    gardiennageSecurite: GardiennageSecurite.empty,
    architecte: Architecte.empty,
    nettoyageIndustriel: NettoyageIndustriel.empty,
    expertComptable: ExpertComptable.empty,
    equipementMedical: EquipementMedical.empty,
    formationPro: FormationPro.empty,
    enseignementSuperieur: EnseignementSuperieur.empty,
    garageMecanique: GarageMecanique.empty,
    laboratoireBtp: LaboratoireBtp.empty,
    immobilier: Immobilier.empty,
    menuiserieAluminium: MenuiserieAluminium.empty,
    imprimerie: Imprimerie.empty,
    ingenierieInformatique: IngenierieInformatique.empty,
    materielInformatique: MaterielInformatique.empty,
    electricien: Electricien.empty,
    amenagementFoncier: AmenagementFoncier.empty,
    deratisationDesinsectisation: DeratisationDesinsectisation.empty,
    banque: Banque.empty,
    materiauxConstruction: MateriauxConstruction.empty,
    hotels: Hotels.empty,
    pharmacies: Pharmacies.empty,
    concessionnairesAutomobile: ConcessionnairesAutomobile.empty,
    groupeElectrogene: GroupeElectrogene.empty,
    architectesInterieur: ArchitectesInterieur.empty,
    agencesCommunication: AgencesCommunication.empty,
    materielElectrique: MaterielElectrique.empty,
    materielsBTP: MaterielsBTP.empty,
    materielsIndustriels: MaterielsIndustriels.empty,
    agencesRecrutement: AgencesRecrutement.empty,
    cliniques: Cliniques.empty,
    conseilJuridique: ConseilJuridique.empty,
};

export function createFields<TValues>(self: Field<TValues, FormData>) {
    const properties = createProperties(self, [
        'user',
        'message',
        'categoryId',
        'country',
        'city',
        'phone',
        'firstname',
        'lastname',
        'email',
        'relatedCmsPostId',
        'agenceVoyage',
        'ameublement',
        'carRental',
        'assurance',
        'transit',
        'solar',
        'consultant',
        'officeFurniture',
        'construction',
        'constructionMetallique',
        'froidClimatisation',
        'visaEtude',
        'gardiennageSecurite',
        'architecte',
        'nettoyageIndustriel',
        'expertComptable',
        'equipementMedical',
        'formationPro',
        'enseignementSuperieur',
        'garageMecanique',
        'laboratoireBtp',
        'immobilier',
        'menuiserieAluminium',
        'imprimerie',
        'ingenierieInformatique',
        'materielInformatique',
        'electricien',
        'amenagementFoncier',
        'deratisationDesinsectisation',
        'banque',
        'materiauxConstruction',
        'hotels',
        'pharmacies',
        'concessionnairesAutomobile',
        'groupeElectrogene',
        'architectesInterieur',
        'agencesCommunication',
        'materielElectrique',
        'materielsBTP',
        'materielsIndustriels',
        'agencesRecrutement',
        'cliniques',
        'conseilJuridique',
    ] as const);

    return {
        self: self,
        ...properties,
        agenceVoyage: AgenceVoyage.createFields(properties.agenceVoyage),
        ameublement: Ameublement.createFields(properties.ameublement),
        carRental: CarRental.createFields(properties.carRental),
        assurance: Assurance.createFields(properties.assurance),
        transit: Transit.createFields(properties.transit),
        solar: Solar.createFields(properties.solar),
        consultant: Consultant.createFields(properties.consultant),
        officeFurniture: OfficeFurniture.createFields(properties.officeFurniture),
        construction: Construction.createFields(properties.construction),
        constructionMetallique: ConstructionMetallique.createFields(
            properties.constructionMetallique,
        ),
        froidClimatisation: FroidClimatisation.createFields(
            properties.froidClimatisation,
        ),
        visaEtude: VisaEtude.createFields(properties.visaEtude),
        gardiennageSecurite: GardiennageSecurite.createFields(
            properties.gardiennageSecurite,
        ),
        architecte: Architecte.createFields(properties.architecte),
        nettoyageIndustriel: NettoyageIndustriel.createFields(
            properties.nettoyageIndustriel,
        ),
        expertComptable: ExpertComptable.createFields(properties.expertComptable),
        equipementMedical: EquipementMedical.createFields(properties.equipementMedical),
        formationPro: FormationPro.createFields(properties.formationPro),
        enseignementSuperieur: EnseignementSuperieur.createFields(
            properties.enseignementSuperieur,
        ),
        garageMecanique: GarageMecanique.createFields(properties.garageMecanique),
        laboratoireBtp: LaboratoireBtp.createFields(properties.laboratoireBtp),
        immobilier: Immobilier.createFields(properties.immobilier),
        menuiserieAluminium: MenuiserieAluminium.createFields(
            properties.menuiserieAluminium,
        ),
        imprimerie: Imprimerie.createFields(properties.imprimerie),
        ingenierieInformatique: IngenierieInformatique.createFields(
            properties.ingenierieInformatique,
        ),
        materielInformatique: MaterielInformatique.createFields(
            properties.materielInformatique,
        ),
        electricien: Electricien.createFields(properties.electricien),
        amenagementFoncier: AmenagementFoncier.createFields(
            properties.amenagementFoncier,
        ),
        deratisationDesinsectisation: DeratisationDesinsectisation.createFields(
            properties.deratisationDesinsectisation,
        ),
        banque: Banque.createFields(properties.banque),
        materiauxConstruction: MateriauxConstruction.createFields(
            properties.materiauxConstruction,
        ),
        hotels: Hotels.createFields(properties.hotels),
        pharmacies: Pharmacies.createFields(properties.pharmacies),
        concessionnairesAutomobile: ConcessionnairesAutomobile.createFields(
            properties.concessionnairesAutomobile,
        ),
        groupeElectrogene: GroupeElectrogene.createFields(properties.groupeElectrogene),
        architectesInterieur: ArchitectesInterieur.createFields(
            properties.architectesInterieur,
        ),
        agencesCommunication: AgencesCommunication.createFields(
            properties.agencesCommunication,
        ),
        materielElectrique: MaterielElectrique.createFields(
            properties.materielElectrique,
        ),
        materielsBTP: MaterielsBTP.createFields(properties.materielsBTP),
        materielsIndustriels: MaterielsIndustriels.createFields(
            properties.materielsIndustriels,
        ),
        agencesRecrutement: AgencesRecrutement.createFields(
            properties.agencesRecrutement,
        ),
        cliniques: Cliniques.createFields(properties.cliniques),
        conseilJuridique: ConseilJuridique.createFields(properties.conseilJuridique),
    };
}

export function validate(
    form: Form<FormData, string>,
    fields: Fields<FormData>,
    state: FormState<FormData, string>,
) {
    const values = state.values;

    if (values.user == null) {
        state = notBlankAndNotNull(values.firstname, {
            form,
            field: fields.firstname,
            message: 'Vous devez saisir votre prénom',
        })(state);

        state = notBlankAndNotNull(values.lastname, {
            form,
            field: fields.lastname,
            message: 'Vous devez saisir votre nom',
        })(state);

        state = validateEmail(values.email, {
            form,
            field: fields.email,
            notBlank: true,
            message: 'Vous devez saisir une adresse email valide',
        })(state);
    }

    state = notBlankAndNotNull(values.message, {
        form,
        field: fields.message,
        message: 'Vous devez saisir du texte comme message',
    })(state);

    state = notBlankAndNotNull(values.country, {
        form,
        field: fields.country,
        message: 'Vous devez choisir un pays',
    })(state);

    state = notNull(values.categoryId, {
        form,
        field: fields.categoryId,
        message: 'Vous devez specifier une catégorie',
    })(state);

    state = AgenceVoyage.validateAgenceVoyage(
        form,
        values.agenceVoyage,
        fields.agenceVoyage,
    )(state);
    state = Ameublement.validateAmeublement(
        form,
        values.ameublement,
        fields.ameublement,
    )(state);
    state = CarRental.validateCarRental(form, values.carRental, fields.carRental)(state);
    state = Assurance.validateAssurance(form, values.assurance, fields.assurance)(state);
    state = Transit.validateTransit(form, values.transit, fields.transit)(state);
    state = Solar.validateSolar(form, values.solar, fields.solar)(state);

    state = Consultant.validateConsultant(
        form,
        values.consultant,
        fields.consultant,
    )(state);
    state = OfficeFurniture.validateOfficeFurniture(
        form,
        values.officeFurniture,
        fields.officeFurniture,
    )(state);
    state = Construction.validateConstruction(
        form,
        values.construction,
        fields.construction,
    )(state);
    state = VisaEtude.validateVisaEtude(form, values.visaEtude, fields.visaEtude)(state);
    state = GardiennageSecurite.validateGardiennageSecurite(
        form,
        values.gardiennageSecurite,
        fields.gardiennageSecurite,
    )(state);
    state = Architecte.validateArchitecte(
        form,
        values.architecte,
        fields.architecte,
    )(state);
    state = NettoyageIndustriel.validateNettoyageIndustriel(
        form,
        values.nettoyageIndustriel,
        fields.nettoyageIndustriel,
    )(state);
    state = ExpertComptable.validateExpertComptable(
        form,
        values.expertComptable,
        fields.expertComptable,
    )(state);
    state = EquipementMedical.validateEquipementMedical(
        form,
        values.equipementMedical,
        fields.equipementMedical,
    )(state);

    state = FormationPro.validateFormationPro(
        form,
        values.formationPro,
        fields.formationPro,
    )(state);

    state = EnseignementSuperieur.validateEnseignementSuperieur(
        form,
        values.enseignementSuperieur,
        fields.enseignementSuperieur,
    )(state);

    state = GarageMecanique.validateGarageMecanique(
        form,
        values.garageMecanique,
        fields.garageMecanique,
    )(state);

    state = LaboratoireBtp.validateLaboratoireBtp(
        form,
        values.laboratoireBtp,
        fields.laboratoireBtp,
    )(state);

    state = Immobilier.validateImmobilier(
        form,
        values.immobilier,
        fields.immobilier,
    )(state);

    state = ConstructionMetallique.validateConstructionMetallique(
        form,
        values.constructionMetallique,
        fields.constructionMetallique,
    )(state);

    state = FroidClimatisation.validateFroidClimatisation(
        form,
        values.froidClimatisation,
        fields.froidClimatisation,
    )(state);

    state = Imprimerie.validateImprimerie(
        form,
        values.imprimerie,
        fields.imprimerie,
    )(state);

    state = IngenierieInformatique.validateIngenierieInformatique(
        form,
        values.ingenierieInformatique,
        fields.ingenierieInformatique,
    )(state);

    state = MaterielInformatique.validateMaterielInformatique(
        form,
        values.materielInformatique,
        fields.materielInformatique,
    )(state);

    state = Electricien.validateElectricien(
        form,
        values.electricien,
        fields.electricien,
    )(state);

    state = AmenagementFoncier.validateAmenagementFoncier(
        form,
        values.amenagementFoncier,
        fields.amenagementFoncier,
    )(state);

    state = DeratisationDesinsectisation.validateDeratisationDesinsectisation(
        form,
        values.deratisationDesinsectisation,
        fields.deratisationDesinsectisation,
    )(state);

    state = Banque.validateBanque(form, values.banque, fields.banque)(state);

    state = MateriauxConstruction.validateMateriauxConstruction(
        form,
        values.materiauxConstruction,
        fields.materiauxConstruction,
    )(state);

    state = Hotels.validateHotels(form, values.hotels, fields.hotels)(state);

    state = Pharmacies.validatePharmacies(
        form,
        values.pharmacies,
        fields.pharmacies,
    )(state);

    state = ConcessionnairesAutomobile.validateConcessionnairesAutomobile(
        form,
        values.concessionnairesAutomobile,
        fields.concessionnairesAutomobile,
    )(state);

    state = GroupeElectrogene.validateGroupeElectrogene(
        form,
        values.groupeElectrogene,
        fields.groupeElectrogene,
    )(state);

    state = ArchitectesInterieur.validateArchitectesInterieur(
        form,
        values.architectesInterieur,
        fields.architectesInterieur,
    )(state);

    state = AgencesCommunication.validateAgencesCommunication(
        form,
        values.agencesCommunication,
        fields.agencesCommunication,
    )(state);

    state = MaterielElectrique.validateMaterielElectrique(
        form,
        values.materielElectrique,
        fields.materielElectrique,
    )(state);

    state = MaterielsBTP.validateMaterielsBTP(
        form,
        values.materielsBTP,
        fields.materielsBTP,
    )(state);

    state = MaterielsIndustriels.validateMaterielsIndustriels(
        form,
        values.materielsIndustriels,
        fields.materielsIndustriels,
    )(state);

    state = AgencesRecrutement.validateAgencesRecrutement(
        form,
        values.agencesRecrutement,
        fields.agencesRecrutement,
    )(state);

    state = Cliniques.validateCliniques(form, values.cliniques, fields.cliniques)(state);

    state = ConseilJuridique.validateConseilJuridique(
        form,
        values.conseilJuridique,
        fields.conseilJuridique,
    )(state);

    return state;
}

export function createForm(
    id: string,
    initialValues: Partial<FormData> = empty,
): [Form<FormData, string>, Fields<FormData>] {
    const fields = createFields<FormData>(createRoot<FormData>());
    const form = new Form<FormData, string>(
        { initialValues: mergeObject(empty, initialValues) },
        id,
    );

    form.addValidator((state) => {
        return validate(form, fields, state);
    });

    return [form, fields];
}

type OnSubmit = (
    form: Form<FormData, string>,
) => Promise<Result<MultiQuote_CreateMutationMutation['multiquote']['create'], string>>;

type ExtraValueScalar =
    | string
    | number
    | null
    | undefined
    | CalendarDateTime
    | CalendarDate;
type ExtraValue = ExtraValueScalar | readonly ExtraValueScalar[];

function isExtraValueArray(arg: ExtraValue): arg is readonly ExtraValueScalar[] {
    return Array.isArray(arg);
}

function formatExtraValue(
    deps: Pick<ShareableContainer, 'intl'>,
    value: ExtraValue,
): string {
    const { intl } = deps;

    if (value instanceof CalendarDateTime) {
        return intl().formatCalendarDateTime(value, 'PPPp');
    } else if (value instanceof CalendarDate) {
        return intl().formatCalendarDate(value, 'PPP');
    } else if (isExtraValueArray(value)) {
        return value.map((v) => formatExtraValue(deps, v)).join(', ');
    } else if (typeof value === 'number') {
        return intl().formatInt(value);
    } else if (typeof value === 'string') {
        return value;
    } else {
        return '';
    }
}

function formatExtraFields(
    deps: Pick<ShareableContainer, 'intl'>,
    fields: readonly Readonly<[string, ExtraValue]>[],
): { label: string; value: string }[] {
    const lines: { label: string; value: string }[] = [];
    for (const [label, value] of fields) {
        if (value != null) {
            lines.push({ label: label, value: formatExtraValue(deps, value) });
        }
    }

    return lines;
}

const categorySisterSets: readonly { nom: string; categories: readonly number[] }[] = [
    { nom: 'Assurances et courtiers en assurance', categories: [173, 174] },
    { nom: 'Transit et Transit maritime', categories: [310, 323, 325] },
    { nom: 'Construction', categories: [59, 67, 430] },
    { nom: 'Gardiennage / Alarme / Surveillance', categories: [253, 254, 616, 645] },
    { nom: 'Préinscription - Visa', categories: [614, 700] },
    { nom: 'Expert comptable / CAC', categories: [139, 140, 144] },
    { nom: "Bureau d'études - Cabinets d'audit", categories: [139, 143] },
    { nom: 'Immobilier', categories: [195, 196, 340, 499] },
    { nom: 'Architectes', categories: [54, 150] },
    { nom: 'Equipements médicaux', categories: [239, 250] },
    { nom: 'Froid climatisation', categories: [55, 205] },
    { nom: 'Imprimeries', categories: [124, 134, 137, 563] },
    { nom: 'Matériel informatique', categories: [276, 281, 381, 602] },
    { nom: 'Ingénierie informatique', categories: [279, 282, 697, 704] },
    { nom: 'Genie electrique', categories: [169, 527] },
    { nom: 'Banques', categories: [175, 179, 518] },
    { nom: 'Matériaux de construction', categories: [60, 62, 68, 77, 78, 338] },
    { nom: 'Hotels', categories: [286, 303, 365, 687] },
    { nom: 'Concessionnaire auto moto', categories: [39, 40, 43, 47, 414, 599] },
    { nom: "Architecte d'intérieur", categories: [633, 692] },
    { nom: 'Agence de communication', categories: [123, 130, 151, 540, 546, 563] },
    {
        nom: 'Matériels et équipements industriels',
        categories: [204, 219, 455, 598, 646],
    },
    { nom: 'Agence de recrutement', categories: [154, 157, 159, 532] },
    { nom: 'Cliniques', categories: [235, 237, 238] },
    { nom: 'Conseil juridique', categories: [141, 142] },
    { nom: 'Garages / Mécanique', categories: [41, 45, 349, 636] },
];

function makeUseForCategoryId(categoryId: number) {
    const set =
        categorySisterSets.find((s) => s.categories.includes(categoryId))?.categories ??
        [];

    return function useForCategoryId(cId: number): boolean {
        return cId === categoryId || set.includes(cId);
    };
}

type CategoryStrategy = {
    useForCategoryId(categoryId: number): boolean;
    formatFields(
        deps: Pick<ShareableContainer, 'intl'>,
        values: FormData,
    ): JsonValue | null;
    extraComponentForm: typeof CarRentalForm;
};

function makeStrategy({
    categoryId,
    formatFields,
    extraComponentForm,
}: {
    categoryId: number;
    formatFields: CategoryStrategy['formatFields'];
    extraComponentForm: CategoryStrategy['extraComponentForm'];
}): CategoryStrategy {
    const useForCategoryId = makeUseForCategoryId(categoryId);

    return {
        useForCategoryId,
        formatFields,
        extraComponentForm,
    };
}

const categoryStrategies: readonly CategoryStrategy[] = [
    makeStrategy({
        categoryId: 124,
        formatFields(deps, values) {
            const imprimerie = values.imprimerie;
            return formatExtraFields(deps, [
                [
                    `Quel type de service d'impression recherchez-vous ?`,
                    imprimerie.serviceType,
                ],
                [
                    `Quels types de produits souhaitez-vous imprimer ?`,
                    imprimerie.productTypes,
                ],
                [
                    `Quel est le volume d'impression estimé pour votre projet ?`,
                    imprimerie.printVolume,
                ],
                [
                    `Quel est votre délai de livraison souhaité pour ce projet ?`,
                    imprimerie.deliveryDeadline,
                ],
            ]);
        },
        extraComponentForm: ImprimerieForm,
    }),
    makeStrategy({
        categoryId: 282,
        formatFields(deps, values) {
            const ingenierieInformatique = values.ingenierieInformatique;
            return formatExtraFields(deps, [
                [
                    `Quels types de services informatiques recherchez-vous ?`,
                    ingenierieInformatique.serviceTypes,
                ],
                [
                    `Avez-vous un projet spécifique en tête ou avez-vous besoin de conseils pour définir vos besoins ?`,
                    ingenierieInformatique.projectType,
                ],
                [
                    `Quelle est la taille de votre entreprise ou organisation ?`,
                    ingenierieInformatique.companySize,
                ],
            ]);
        },
        extraComponentForm: IngenierieInformatiqueForm,
    }),
    makeStrategy({
        categoryId: 381,
        formatFields(deps, values) {
            const materielInformatique = values.materielInformatique;
            return formatExtraFields(deps, [
                [
                    `Quel type de matériel informatique recherchez-vous principalement ?`,
                    materielInformatique.equipmentType,
                ],
                [
                    `Quel est votre principal objectif avec ce matériel informatique ?`,
                    materielInformatique.primaryObjective,
                ],
                [
                    `Quelle est la taille de votre entreprise ou organisation ?`,
                    materielInformatique.companySize,
                ],
                [
                    `Avez-vous besoin de services supplémentaires en plus de l'achat de matériel ?`,
                    materielInformatique.additionalServices,
                ],
            ]);
        },
        extraComponentForm: MaterielInformatiqueForm,
    }),
    makeStrategy({
        categoryId: 527,
        formatFields(deps, values) {
            const electricien = values.electricien;
            return formatExtraFields(deps, [
                [
                    `Quel type de projet électrique envisagez-vous ?`,
                    electricien.projectType,
                ],
                [`Quels services recherchez-vous principalement ?`, electricien.services],
                [
                    `Avez-vous des exigences spécifiques en matière de produits ou de marques ?`,
                    electricien.productRequirements,
                ],
            ]);
        },
        extraComponentForm: ElectricienForm,
    }),
    makeStrategy({
        categoryId: 682,
        formatFields(deps, values) {
            const amenagementFoncier = values.amenagementFoncier;
            return formatExtraFields(deps, [
                [
                    `Quel type de projet foncier ou immobilier envisagez-vous ?`,
                    amenagementFoncier.projectType,
                ],
                [
                    `Quelle est la localisation géographique de votre projet ?`,
                    amenagementFoncier.geographicLocation,
                ],
                [
                    `Quels services spécifiques recherchez-vous pour votre projet ?`,
                    amenagementFoncier.specificServices,
                ],
            ]);
        },
        extraComponentForm: AmenagementFoncierForm,
    }),
    makeStrategy({
        categoryId: 576,
        formatFields(deps, values) {
            const deratisationDesinsectisation = values.deratisationDesinsectisation;
            return formatExtraFields(deps, [
                [
                    `Quel type de nuisibles souhaitez-vous traiter ?`,
                    deratisationDesinsectisation.pestType,
                ],
                [
                    `Quelle est la nature de votre établissement ou espace à traiter ?`,
                    deratisationDesinsectisation.establishmentType,
                ],
                [
                    `Quel est le niveau d'urgence de votre besoin ?`,
                    deratisationDesinsectisation.urgencyLevel,
                ],
                [
                    `Souhaitez-vous un traitement ponctuel ou un contrat de maintenance régulier ?`,
                    deratisationDesinsectisation.treatmentType,
                ],
            ]);
        },
        extraComponentForm: DeratisationDesinsectisationForm,
    }),
    makeStrategy({
        categoryId: 175,
        formatFields(deps, values) {
            const banque = values.banque;
            return formatExtraFields(deps, [
                [
                    `Quel type de service bancaire recherchez-vous actuellement ?`,
                    banque.serviceType,
                ],
                [
                    `Quel type de compte bancaire vous intéresse le plus ?`,
                    banque.accountTypes,
                ],
                [
                    `Avez-vous besoin d'un service de crédit ou de financement ?`,
                    banque.creditNeed,
                ],
                [
                    `Quel est votre mode de communication préféré pour interagir avec votre banque ?`,
                    banque.communicationMode,
                ],
            ]);
        },
        extraComponentForm: BanqueForm,
    }),
    makeStrategy({
        categoryId: 68,
        formatFields(deps, values) {
            const materiauxConstruction = values.materiauxConstruction;
            return formatExtraFields(deps, [
                [
                    `Quels matériaux de construction recherchez-vous principalement ?`,
                    materiauxConstruction.materials,
                ],
                [
                    `Quel type de projet de construction ou de rénovation envisagez-vous ?`,
                    materiauxConstruction.projectType,
                ],
            ]);
        },
        extraComponentForm: MateriauxConstructionForm,
    }),
    makeStrategy({
        categoryId: 303,
        formatFields(deps, values) {
            const hotels = values.hotels;
            return formatExtraFields(deps, [
                [`Quel type de séjour recherchez-vous ?`, hotels.stayType],
                [
                    `Quelles sont les commodités essentielles pour vous lors de votre séjour à l'hôtel ?`,
                    hotels.essentialAmenities,
                ],
                [
                    `Dates de début votre séjour ? (même approximatives)`,
                    hotels.checkInDate,
                ],
                [`Date de fin de votre séjour ?`, hotels.checkOutDate],
                [`Gamme de l'hôtel`, hotels.hotelRating],
            ]);
        },
        extraComponentForm: HotelsForm,
    }),
    makeStrategy({
        categoryId: 231,
        formatFields(deps, values) {
            const pharmacies = values.pharmacies;
            return formatExtraFields(deps, [
                [
                    `Quels types de produits ou services recherchez-vous principalement dans une pharmacie ?`,
                    pharmacies.productTypes,
                ],
                [
                    `À quelle fréquence achetez-vous des produits pharmaceutiques ou parapharmaceutiques ?`,
                    pharmacies.purchaseFrequency,
                ],
                [
                    `Êtes-vous intéressé par des conseils ou des services supplémentaires liés à la santé et au bien-être ?`,
                    pharmacies.healthServices,
                ],
            ]);
        },
        extraComponentForm: PharmaciesForm,
    }),
    makeStrategy({
        categoryId: 47,
        formatFields(deps, values) {
            const concessionnairesAutomobile = values.concessionnairesAutomobile;
            return formatExtraFields(deps, [
                [
                    `Quel type de véhicule recherchez-vous ?`,
                    concessionnairesAutomobile.vehicleType,
                ],
                [
                    `Quelle marque de véhicule vous intéresse le plus ?`,
                    concessionnairesAutomobile.brand,
                ],
                [
                    `Quel est votre budget approximatif pour l'achat d'un véhicule ?`,
                    concessionnairesAutomobile.budget,
                ],
                [
                    `Quels services supplémentaires vous intéressent ?`,
                    concessionnairesAutomobile.additionalServices,
                ],
            ]);
        },
        extraComponentForm: ConcessionnairesAutomobileForm,
    }),
    makeStrategy({
        categoryId: 169,
        formatFields(deps, values) {
            const groupeElectrogene = values.groupeElectrogene;
            return formatExtraFields(deps, [
                [
                    `Quel est le type de projet pour lequel vous avez besoin d'un groupe électrogène ?`,
                    groupeElectrogene.projectType,
                ],
                [
                    `Quelle est la capacité en KVA que vous recherchez pour votre groupe électrogène ?`,
                    groupeElectrogene.capacity,
                ],
                [`Quel type de service recherchez-vous ?`, groupeElectrogene.serviceType],
                [
                    `Avez-vous des préférences pour une marque spécifique de groupe électrogène ?`,
                    groupeElectrogene.brandPreference,
                ],
                [`Quel type de carburant préférez-vous ?`, groupeElectrogene.fuelType],
            ]);
        },
        extraComponentForm: GroupeElectrogeneForm,
    }),
    makeStrategy({
        categoryId: 692,
        formatFields(deps, values) {
            const architectesInterieur = values.architectesInterieur;
            return formatExtraFields(deps, [
                [
                    `Quel type de projet d'aménagement intérieur envisagez-vous ?`,
                    architectesInterieur.projectType,
                ],
                [
                    `Quels services recherchez-vous principalement ?`,
                    architectesInterieur.services,
                ],
                [`Quel style ou ambiance recherchez-vous ?`, architectesInterieur.style],
            ]);
        },
        extraComponentForm: ArchitectesInterieurForm,
    }),
    makeStrategy({
        categoryId: 123,
        formatFields(deps, values) {
            const agencesCommunication = values.agencesCommunication;
            return formatExtraFields(deps, [
                [
                    `Quel type de services recherchez-vous ?`,
                    agencesCommunication.servicesRecherches,
                ],
                [
                    `Quel est l'objectif principal de votre projet ?`,
                    agencesCommunication.objectifPrincipal,
                ],
                [
                    `Dans quel secteur évolue votre activité ?`,
                    agencesCommunication.secteurActivite,
                ],
            ]);
        },
        extraComponentForm: AgencesCommunicationForm,
    }),
    makeStrategy({
        categoryId: 592,
        formatFields(deps, values) {
            const materielElectrique = values.materielElectrique;
            return formatExtraFields(deps, [
                [
                    `Quel type de matériel électrique recherchez-vous ?`,
                    materielElectrique.equipmentType,
                ],
                [
                    `Quel est votre principal objectif d'achat ?`,
                    materielElectrique.purchaseObjective,
                ],
                [
                    `Quel est le volume ou la quantité de matériel dont vous avez besoin ?`,
                    materielElectrique.quantity,
                ],
                [
                    `Avez-vous besoin de conseils ou d'une assistance technique pour votre projet ?`,
                    materielElectrique.technicalAssistance,
                ],
            ]);
        },
        extraComponentForm: MaterielElectriqueForm,
    }),
    makeStrategy({
        categoryId: 593,
        formatFields(deps, values) {
            const materielsBTP = values.materielsBTP;
            return formatExtraFields(deps, [
                [
                    `Quel type de projet de construction ou d'infrastructure envisagez-vous ?`,
                    materielsBTP.projectType,
                ],
                [
                    `Quels types d'équipements ou de matériels recherchez-vous spécifiquement ?`,
                    materielsBTP.equipmentTypes,
                ],
                [
                    `Souhaitez-vous acheter ou louer vos équipements ?`,
                    materielsBTP.purchaseMode,
                ],
                [
                    `Avez-vous besoin de services complémentaires ? (cases à cocher)`,
                    materielsBTP.additionalServices,
                ],
            ]);
        },
        extraComponentForm: MaterielsBTPForm,
    }),
    makeStrategy({
        categoryId: 598,
        formatFields(deps, values) {
            const materielsIndustriels = values.materielsIndustriels;
            return formatExtraFields(deps, [
                [
                    `Quel type de matériel recherchez-vous ?`,
                    materielsIndustriels.materialType,
                ],
                [
                    `Souhaitez-vous acheter ou louer ce matériel ?`,
                    materielsIndustriels.purchaseMode,
                ],
                [
                    `Quel est le volume ou l'urgence de votre besoin ?`,
                    materielsIndustriels.volumeUrgency,
                ],
            ]);
        },
        extraComponentForm: MaterielsIndustrielsForm,
    }),
    makeStrategy({
        categoryId: 157,
        formatFields(deps, values) {
            const agencesRecrutement = values.agencesRecrutement;
            return formatExtraFields(deps, [
                [
                    `Quel type de besoin en recrutement avez-vous actuellement ?`,
                    agencesRecrutement.recruitmentNeed,
                ],
                [
                    `Combien de postes souhaitez-vous pourvoir ?`,
                    agencesRecrutement.positionCount,
                ],
                [
                    `Quel niveau de profil recherchez-vous principalement ?`,
                    agencesRecrutement.profileLevel,
                ],
            ]);
        },
        extraComponentForm: AgencesRecrutementForm,
    }),
    makeStrategy({
        categoryId: 238,
        formatFields(deps, values) {
            const cliniques = values.cliniques;
            return formatExtraFields(deps, [
                [
                    `Quel type de service médical recherchez-vous ?`,
                    cliniques.medicalService,
                ],
                [
                    `Avez-vous besoin d'une consultation urgente ou d'un rendez-vous programmé ?`,
                    cliniques.consultationType,
                ],
                [
                    `Souhaitez-vous des informations sur les services d'hospitalisation ou de soins intensifs ?`,
                    cliniques.hospitalizationInfo,
                ],
            ]);
        },
        extraComponentForm: CliniquesForm,
    }),
    makeStrategy({
        categoryId: 141,
        formatFields(deps, values) {
            const conseilJuridique = values.conseilJuridique;
            return formatExtraFields(deps, [
                [
                    `Quel type de conseil recherchez-vous principalement ?`,
                    conseilJuridique.conseilType,
                ],
                [
                    `Quels sont les principaux défis ou besoins auxquels vous faites face actuellement ?`,
                    conseilJuridique.defisBesoins,
                ],
                [`Quel est votre profil ?`, conseilJuridique.profil],
            ]);
        },
        extraComponentForm: ConseilJuridiqueForm,
    }),
    makeStrategy({
        categoryId: 86,
        formatFields(deps, values) {
            const ameublement = values.ameublement;
            return formatExtraFields(deps, [
                [
                    'Quel type de mobilier recherchez-vous principalement ?',
                    ameublement.furnitureType,
                ],
                [
                    'Êtes-vous intéressé par des meubles sur mesure ou préférez-vous des modèles préfabriqués ?',
                    ameublement.customOrPrefabricated,
                ],
                [
                    'Quel est votre style de décoration préféré ?',
                    ameublement.decorationStyle,
                ],
                [
                    'Avez-vous besoin de services supplémentaires tels que la livraison, le montage ou le conseil en décoration ?',
                    ameublement.additionalServices,
                ],
            ]);
        },
        extraComponentForm: AmeublementForm,
    }),
    makeStrategy({
        categoryId: 55,
        formatFields(deps, values) {
            const froidClimatisation = values.froidClimatisation;
            return formatExtraFields(deps, [
                ['Quel est votre besoin principal ?', froidClimatisation.mainNeed],
                ['Quel type de bâtiment est concerné ?', froidClimatisation.buildingType],
                [
                    'Quelle est la surface approximative du lieu concerné en m2 ?',
                    froidClimatisation.surfaceArea,
                ],
                ["Quel est la ville de l'intervention ?", froidClimatisation.city],
                [
                    'Quand souhaitez-vous réaliser ce projet ?',
                    froidClimatisation.projectTimeline,
                ],
            ]);
        },
        extraComponentForm: FroidClimatisationForm,
    }),
    makeStrategy({
        categoryId: 65,
        formatFields(deps, values) {
            const constructionMetallique = values.constructionMetallique;
            return formatExtraFields(deps, [
                [
                    'Quel est le type de projet concerné ?',
                    constructionMetallique.projectType,
                ],
                [
                    'Quel usage est prévu pour la structure métallique ?',
                    constructionMetallique.structureUsage,
                ],
                [
                    'Quelle est la surface estimée de la structure ?',
                    constructionMetallique.surfaceArea,
                ],
                [
                    "Souhaitez-vous uniquement la fabrication, ou aussi l'installation ?",
                    constructionMetallique.serviceType,
                ],
                [
                    "Avez-vous besoin d'une prestation complémentaire ?",
                    constructionMetallique.additionalServices,
                ],
            ]);
        },
        extraComponentForm: ConstructionMetalliqueForm,
    }),
    makeStrategy({
        categoryId: 284,
        formatFields(deps, values) {
            const agenceVoyage = values.agenceVoyage;
            return formatExtraFields(deps, [
                [
                    'Quel type de voyage souhaitez-vous planifier ?',
                    agenceVoyage.travelType,
                ],
                ['Quel service vous intéresse le plus ?', agenceVoyage.serviceType],
                [
                    "Quelle est votre destination ou votre pays d'arrivée ?",
                    agenceVoyage.destination,
                ],
                ['Dates souhaitées aller', agenceVoyage.travelDateStart],
                ['Dates souhaitées retour', agenceVoyage.travelDateEnd],
            ]);
        },
        extraComponentForm: AgenceVoyageForm,
    }),
    makeStrategy({
        categoryId: 499,
        formatFields(deps, values) {
            const immobilier = values.immobilier;
            return formatExtraFields(deps, [
                ['Quel type de bien recherchez-vous ?', immobilier.propertyType],
                ['Quel est votre objectif ?', immobilier.objective],
            ]);
        },
        extraComponentForm: ImmobilierForm,
    }),
    makeStrategy({
        categoryId: 41,
        formatFields(deps, values) {
            const garageMecanique = values.garageMecanique;
            return formatExtraFields(deps, [
                ['Quel type de service recherchez-vous ?', garageMecanique.serviceTypes],
                ['Pour quel type de véhicule ?', garageMecanique.vehicleType],
                [
                    'Quelle est la marque et le modèle de votre véhicule ?',
                    garageMecanique.vehicleBrandModel,
                ],
                ['Année du véhicule', garageMecanique.vehicleYear],
            ]);
        },
        extraComponentForm: GarageMecaniqueForm,
    }),
    makeStrategy({
        categoryId: 411,
        formatFields(deps, values) {
            const carRental = values.carRental;
            return formatExtraFields(deps, [
                ['Type de véhicule', carRental.vehicleType],
                ['Capacité du véhicule', carRental.vehicleCapacity],
                ['Chauffeur ?', carRental.withDriver],
                ['Lieu de prise en charge', carRental.pickupLocation],
                ['Date et heure de départ', carRental.departureDate],
                ['Date et heure de retour', carRental.returnDate],
            ]);
        },
        extraComponentForm: CarRentalForm,
    }),
    makeStrategy({
        categoryId: 173,
        formatFields(deps, values) {
            const assurance = values.assurance;
            return formatExtraFields(deps, [
                ['Type d’assurance recherchée', assurance.assuranceType],
                [
                    'Êtes-vous un particulier ou un professionnel',
                    assurance.isIndividualOrProfessional,
                ],
                ...(assurance.assuranceType === 'other'
                    ? ([
                          [`Précisez le type d'assurance`, assurance.assuranceTypeOther],
                      ] as const)
                    : []),
                ...(assurance.assuranceType === 'auto'
                    ? ([
                          ['Type de véhicule', assurance.vehicleType],
                          ['Marque et modèle', assurance.vehicleBrandAndModel],
                          ['Année de mise en circulation', assurance.vehicleYear],
                          ['Usage du véhicule', assurance.vehicleUsage],
                          ['Garantie souhaitée', assurance.desiredCoverage],
                      ] as const)
                    : []),
                ...(assurance.assuranceType === 'home'
                    ? ([
                          ['Type de logement', assurance.housingType],
                          ['Propriétaire ou Locataire ?', assurance.isOwnerOrTenant],
                          ['Surface du logement', assurance.housingSurface],
                          ['Valeur du bien à assurer', assurance.insuredGoodsValue],
                      ] as const)
                    : []),
                ...(assurance.assuranceType === 'health'
                    ? ([
                          ['Âge du souscripteur', assurance.subscriberAge],
                          [
                              'Nombre de personnes à couvrir',
                              assurance.numberOfPeopleCovered,
                          ],
                          [
                              'Niveau de remboursement souhaité',
                              assurance.desiredReimbursementLevel,
                          ],
                      ] as const)
                    : []),
                ...(assurance.assuranceType === 'pro'
                    ? ([
                          ['Secteur d’activité', assurance.activitySector],
                          ['Nombre d’employés', assurance.employeeCount],
                          ['Type de couverture souhaitée', assurance.coverageType],
                      ] as const)
                    : []),
                ...(assurance.assuranceType === 'travel'
                    ? ([
                          ['Destination du voyage', assurance.travelDestination],
                          ['Durée du séjour', assurance.travelDuration],
                          [
                              'Nombre de personnes à couvrir',
                              assurance.travelNumberOfPeople,
                          ],
                      ] as const)
                    : []),
                ...(assurance.assuranceType === 'pet'
                    ? ([
                          ['Type d’animal', assurance.animalType],
                          ['Race de l’animal', assurance.animalBreed],
                          ['Âge de l’animal', assurance.animalAge],
                      ] as const)
                    : []),
                ...(assurance.assuranceType === 'life'
                    ? ([
                          [
                              'Quel est votre objectif principal ?',
                              assurance.lifeMainObjective,
                          ],
                          ['Âge du souscripteur', assurance.lifeSubscriberAge],
                          ['Montant estimé à assurer', assurance.lifeEstimatedAmount],
                          [
                              'Bénéficiaire(s) désigné(s) ?',
                              assurance.lifeDesignatedBeneficiary,
                          ],
                      ] as const)
                    : []),
                ...(assurance.assuranceType === 'borrower'
                    ? ([
                          ['Montant du prêt', assurance.borrowerLoanAmount],
                          ['Durée du prêt', assurance.borrowerLoanDuration],
                          [
                              'Type de bien financé',
                              assurance.borrowerPropertyTypeFinanced,
                          ],
                          [
                              'Avez-vous des problèmes de santé connus ?',
                              assurance.borrowerHasKnownHealthIssues,
                          ],
                          [
                              'Souhaitez-vous déléguer votre assurance (hors banque) ?',
                              assurance.borrowerWantsToDelegateInsurance,
                          ],
                      ] as const)
                    : []),
                ...(assurance.assuranceType === 'temporary'
                    ? ([
                          [
                              'Type de bien ou situation à assurer temporairement',
                              assurance.temporaryInsuredItemOrSituation,
                          ],
                          [
                              'Durée souhaitée de l’assurance',
                              assurance.temporaryDesiredDuration,
                          ],
                          [
                              'Motif de la souscription temporaire',
                              assurance.temporarySubscriptionReason,
                          ],
                          [
                              'Date de début de la couverture',
                              assurance.temporaryCoverageStartDate,
                          ],
                      ] as const)
                    : []),
            ]);
        },
        extraComponentForm: AssuranceForm,
    }),
    makeStrategy({
        categoryId: 310,
        formatFields(deps, values) {
            const transit = values.transit;
            return formatExtraFields(deps, [
                [`Quel type de transit recherchez-vous ?`, transit.transitType],
                [
                    `Votre marchandise est-elle déjà disponible ?`,
                    transit.isGoodsAvailable,
                ],
                [
                    `Quel est le point de départ de votre marchandise ?`,
                    transit.goodsStartPoint,
                ],
                [`Quelle est la destination finale ?`, transit.goodsFinalDestination],
                [
                    `Avez-vous besoin d’un service de dédouanement ?`,
                    transit.clearanceServiceRequired,
                ],
                [
                    `Souhaitez-vous un service d’entreposage temporaire ?`,
                    transit.temporaryStorageRequired,
                ],
                [`Quel est le type de marchandise ?`, transit.goodsType],
                [`Quel est le volume estimé de la marchandise ?`, transit.goodsVolume],
                [`Quel est le poids estimé de la marchandise ?`, transit.goodsWeight],
                [`Avez-vous des exigences particulières ?`, transit.specificRequirements],
                [`Quand souhaitez-vous organiser le transit ?`, transit.transitDate],
            ]);
        },
        extraComponentForm: TransitForm,
    }),
    makeStrategy({
        categoryId: 167,
        formatFields(deps, values) {
            const solar = values.solar;
            return formatExtraFields(deps, [
                [`Type de demande`, solar.requestType],
                [
                    `Consommation moyenne ou besoins estimés`,
                    solar.averageConsumptionOrEstimatedNeeds,
                ],
                [`Quel est votre objectif principal ?`, solar.mainObjective],
                [`Quand souhaitez-vous démarrer le projet ?`, solar.projectStartDate],
            ]);
        },
        extraComponentForm: SolarForm,
    }),
    makeStrategy({
        categoryId: 143,
        formatFields(deps, values) {
            const consultant = values.consultant;
            return formatExtraFields(deps, [
                [`Type de besoin`, consultant.needType],
                [`Vous êtes un / une`, consultant.orgType],
            ]);
        },
        extraComponentForm: ConsultantForm,
    }),
    makeStrategy({
        categoryId: 108,
        formatFields(deps, values) {
            const officeFurniture = values.officeFurniture;
            return formatExtraFields(deps, [
                [`Vous êtes`, officeFurniture.entityType],
                [
                    `Quels types de mobilier recherchez-vous ?`,
                    officeFurniture.furnitureTypes,
                ],
                [`Volume estimé ou nombre de postes à équiper`, officeFurniture.volume],
            ]);
        },
        extraComponentForm: OfficeFurnitureForm,
    }),
    makeStrategy({
        categoryId: 59,
        formatFields(deps, values) {
            const construction = values.construction;
            return formatExtraFields(deps, [
                [
                    `Quel type de projet souhaitez-vous réaliser ?`,
                    construction.projectType,
                ],
                [`Pour quel type de bâtiment ?`, construction.buildingType],
            ]);
        },
        extraComponentForm: ConstructionForm,
    }),
    makeStrategy({
        categoryId: 700,
        formatFields(deps, values) {
            const visaEtude = values.visaEtude;
            return formatExtraFields(deps, [
                [`Dans quel pays souhaitez-vous étudier ?`, visaEtude.country],
                [
                    `Quel est votre niveau d’études actuel ?`,
                    visaEtude.currentEducationLevel,
                ],
                [`Quels services vous intéressent ?`, visaEtude.services],
            ]);
        },
        extraComponentForm: VisaEtudeForm,
    }),
    makeStrategy({
        categoryId: 254,
        formatFields(deps, values) {
            const gardiennageSecurite = values.gardiennageSecurite;
            return formatExtraFields(deps, [
                [
                    `Quels sont les lieux ou biens à sécuriser ?`,
                    gardiennageSecurite.placesToSecure,
                ],
                [`Quel type de besoin avez-vous ?`, gardiennageSecurite.needs],
                [
                    `Souhaitez-vous une assistance 24h/24 ?`,
                    gardiennageSecurite.assistance24h,
                ],
            ]);
        },
        extraComponentForm: GardiennageSecuriteForm,
    }),
    makeStrategy({
        categoryId: 54,
        formatFields(deps, values) {
            const architecte = values.architecte;
            return formatExtraFields(deps, [
                [`Quel type de projet souhaitez-vous réaliser ?`, architecte.projectType],
                [`Pour quel type de bâtiment ou d’espace ?`, architecte.buildingType],
            ]);
        },
        extraComponentForm: ArchitecteForm,
    }),
    makeStrategy({
        categoryId: 216,
        formatFields(deps, values) {
            const nettoyageIndustriel = values.nettoyageIndustriel;
            return formatExtraFields(deps, [
                [`Quel est votre besoin principal ?`, nettoyageIndustriel.needType],
                [
                    `À quelle fréquence souhaitez-vous ce service ?`,
                    nettoyageIndustriel.frequency,
                ],
                [
                    `Surface totale à nettoyer (en m² approximatif)`,
                    nettoyageIndustriel.surfaceArea,
                ],
            ]);
        },
        extraComponentForm: NettoyageIndustrielForm,
    }),
    makeStrategy({
        categoryId: 140,
        formatFields(deps, values) {
            const expertComptable = values.expertComptable;
            return formatExtraFields(deps, [
                [
                    'Quel est le statut de votre entreprise ?',
                    expertComptable.businessStatus,
                ],
                ['Quels services recherchez-vous ?', expertComptable.services],
                [
                    'Souhaitez-vous un accompagnement ponctuel ou un suivi régulier ?',
                    expertComptable.supportType,
                ],
            ]);
        },
        extraComponentForm: ExpertComptableForm,
    }),
    makeStrategy({
        categoryId: 239,
        formatFields(deps, values) {
            const equipementMedical = values.equipementMedical;
            return formatExtraFields(deps, [
                ['Vous êtes ?', equipementMedical.youAre],
                ['Matériel médical recherché', equipementMedical.equipmentType],
                [
                    'Souhaitez-vous également un service associé ?',
                    equipementMedical.services,
                ],
            ]);
        },
        extraComponentForm: EquipementMedicalForm,
    }),
    makeStrategy({
        categoryId: 191,
        formatFields(deps, values) {
            const formationPro = values.formationPro;
            return formatExtraFields(deps, [
                [
                    'Dans quel domaine souhaitez-vous vous former ?',
                    formationPro.trainingDomain,
                ],
                [
                    'Quelle est votre principale motivation pour suivre une formation ?',
                    formationPro.mainMotivation,
                ],
                [
                    'Situation professionnelle actuelle, vous êtes :',
                    formationPro.professionalSituation,
                ],
            ]);
        },
        extraComponentForm: FormationProForm,
    }),
    makeStrategy({
        categoryId: 188,
        formatFields(deps, values) {
            const enseignementSuperieur = values.enseignementSuperieur;
            return formatExtraFields(deps, [
                ["Niveau d'études actuel", enseignementSuperieur.currentEducationLevel],
                [
                    'Dans quel domaine souhaitez-vous poursuivre vos études ?',
                    enseignementSuperieur.studyDomains,
                ],
                [
                    'Quel type de formation recherchez-vous ?',
                    enseignementSuperieur.formationType,
                ],
                [
                    'Quand souhaitez-vous commencer votre formation ?',
                    enseignementSuperieur.startDate,
                ],
            ]);
        },
        extraComponentForm: EnseignementSuperieurForm,
    }),
    makeStrategy({
        categoryId: 460,
        formatFields(deps, values) {
            const laboratoireBtp = values.laboratoireBtp;
            return formatExtraFields(deps, [
                [
                    'Quel type de projet souhaitez-vous étudier ?',
                    laboratoireBtp.projectType,
                ],
                ['À quelle étape se trouve votre projet ?', laboratoireBtp.projectStage],
                [
                    'Quels types de prestations recherchez-vous ?',
                    laboratoireBtp.serviceTypes,
                ],
                [
                    'Avez-vous déjà réalisé une étude de sol ou des tests ?',
                    laboratoireBtp.soilStudyDone,
                ],
            ]);
        },
        extraComponentForm: LaboratoireBtpForm,
    }),
    makeStrategy({
        categoryId: 70,
        formatFields(deps, values) {
            const menuiserieAluminium = values.menuiserieAluminium;
            return formatExtraFields(deps, [
                [
                    'Quels produits ou services recherchez-vous ?',
                    menuiserieAluminium.products,
                ],
                [
                    'Quel type de projet souhaitez-vous réaliser ?',
                    menuiserieAluminium.projectType,
                ],
                [
                    'Quel est le niveau de personnalisation souhaité pour votre projet ?',
                    menuiserieAluminium.customizationLevel,
                ],
            ]);
        },
        extraComponentForm: MenuiserieAluminiumForm,
    }),
];

export function getCategoryStrategyForCategoryId(
    categoryId: number | null,
): CategoryStrategy | null {
    if (categoryId == null) {
        return null;
    }
    return categoryStrategies.find((s) => s.useForCategoryId(categoryId)) ?? null;
}

function extractExtraFields(
    deps: Pick<ShareableContainer, 'intl'>,
    values: FormData,
): JsonValue | null {
    const strategy = getCategoryStrategyForCategoryId(values.categoryId);
    if (strategy != null) {
        return strategy.formatFields(deps, values);
    }
    return null;
}

export type MultiQuoteOnSubmitSuccessParams = OnSubmitSuccessParams<
    FormData,
    MultiQuote_CreateMutationMutation['multiquote']['create']
>;

export const createOnSubmit: (
    deps: Pick<ShareableContainer, 'intl' | 'apollo'>,
) => OnSubmit = (deps) => (form) => {
    return temporizePromise(
        1000,
        (async () => {
            try {
                const { apollo } = deps;
                const values = form.getState().values;

                console.log(window.document.location.href);
                console.log(new ImmURL(window.document.location.href));
                console.log(
                    getUtmVariablesFromUrl(new ImmURL(window.document.location.href)),
                );

                const result = await apollo().mutate({
                    mutation: MultiQuote_CreateMutationDocument,
                    variables: {
                        input: {
                            message: values.message,
                            cityId: values.city,
                            phone: values.phone,
                            firstname: values.firstname,
                            lastname: values.lastname,
                            email: values.email,
                            relatedCmsPostId: values.relatedCmsPostId,
                            categoryId: notNullableOrThrow(values.categoryId),
                            country: notNullableOrThrow(values.country),
                            requestInfo: getRequestInfoInput(
                                new URL(window.document.location.href),
                            ),
                            utmVariables: getUtmVariablesFromUrl(
                                new ImmURL(window.document.location.href),
                            ),
                            extraFields: extractExtraFields(deps, values),
                        },
                    },
                });
                if (result.errors != null || result.data == null) {
                    return fail(result.errors?.toString() ?? 'Unknown error');
                }

                return ok(result.data.multiquote.create);
            } catch (e) {
                console.error(e);
                return fail('Erreur inconnue.');
            }
        })(),
    );
};
